import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Calendar, Sparkles, Target, ThumbsUp, ThumbsDown, Download, Paperclip, Newspaper, ArrowLeft, Trash2, Send, UserCheck, Clock, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SponsorBanner } from '@/components/SponsorBanner';
import RichText from '@/components/RichText';
import PageSideRail from '@/components/PageSideRail';
import { toast } from 'sonner';
import SubmitNewsDialog from '@/components/SubmitNewsDialog';

const BACKEND_URL = '';

function ForYouCard({ article, onReact, onDelete, onOpen, isAdmin }) {
  // The /api/news/for-you endpoint strips `content` for performance, so we
  // can't rely on it being present to decide whether to open the in-app
  // reader. Admin-authored articles always have a `news_id`, so the modal
  // opener works regardless. The external link only takes precedence when
  // we know there is no in-app article body (i.e. the listing tells us the
  // story is purely an RSS pointer via `source_type === 'rss'`).
  const externalLink = article.external_url || article.link;
  const isRssOnly = article.source_type === 'rss' && externalLink;
  const handleOpen = (e) => {
    if (e) e.stopPropagation();
    if (isRssOnly) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    } else {
      onOpen(article);
    }
  };
  return (
    <Card className="p-5 relative border border-brand-blue/20 bg-gradient-to-br from-white to-brand-navy/5 hover:shadow-lg transition-all cursor-pointer"
      onClick={handleOpen}
      data-testid={`foryou-card-${article.news_id}`}>
      {isAdmin && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(article); }}
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-white bg-rose-50 hover:bg-rose-500 border border-rose-200 hover:border-rose-500 px-2.5 py-1 rounded-full transition-all shadow-sm z-10"
          data-testid={`delete-foryou-${article.news_id}`}
          title="Remove this article (will not be re-imported)">
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      )}
      <div className="flex gap-4">
        {article.image && (
          <img src={article.image} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-bold text-brand-navy bg-brand-blue/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Target className="h-2.5 w-2.5" /> FOR YOU
            </span>
            <span className="text-xs text-slate-400">{article.source}</span>
          </div>
          <h3 className="text-base font-bold font-heading mb-1 leading-tight line-clamp-2 min-h-[2.5rem] hover:text-brand-navy transition-colors"
            data-testid={`foryou-title-${article.news_id}`}>{article.title}</h3>
          <div className="text-sm text-slate-600 mb-2"><RichText html={article.summary} className="card card-2" /></div>
          {article.match_reasons?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {article.match_reasons.map((r, i) => (
                <span key={i} className="text-[10px] text-brand-navy bg-brand-navy/5 border border-brand-blue/20 px-2 py-0.5 rounded-full">{r}</span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between">
            {(!isRssOnly || externalLink) && (
              <button type="button" onClick={handleOpen}
                className="inline-flex items-center text-sm text-brand-navy hover:text-brand-dark font-medium"
                data-testid={`foryou-read-${article.news_id}`}>
                Read more <ExternalLink className="h-3 w-3 ml-1" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <button
                data-testid={`foryou-thumbsup-${article.news_id}`}
                onClick={(e) => { e.stopPropagation(); onReact(article.news_id, 'up'); }}
                className={`flex items-center gap-1 text-xs transition-colors ${article.my_reaction === 'up' ? 'text-brand-navy font-semibold' : 'text-slate-400 hover:text-brand-navy'}`}
              >
                <ThumbsUp className={`h-3.5 w-3.5 ${article.my_reaction === 'up' ? 'fill-brand-navy' : ''}`} /> {article.thumbs_up || 0}
              </button>
              <button
                data-testid={`foryou-thumbsdown-${article.news_id}`}
                onClick={(e) => { e.stopPropagation(); onReact(article.news_id, 'down'); }}
                className={`flex items-center gap-1 text-xs transition-colors ${article.my_reaction === 'down' ? 'text-red-500 font-semibold' : 'text-slate-400 hover:text-red-400'}`}
              >
                <ThumbsDown className={`h-3.5 w-3.5 ${article.my_reaction === 'down' ? 'fill-red-500' : ''}`} /> {article.thumbs_down || 0}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function NewsPage({ user }) {
  const [news, setNews] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forYouLoading, setForYouLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | sll-members | featured | rss
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const gridTopRef = useRef(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [mySubmissions, setMySubmissions] = useState({ submissions: [], pending_count: 0, max_pending: 3 });
  const [searchParams, setSearchParams] = useSearchParams();
  const articleParam = searchParams.get('article');
  const navigate = useNavigate();
  const isAdminUser = user?.role === 'admin';
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  usePageMeta({ title: 'News' });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/news`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => { setNews(data); setLoading(false); })
      .catch(() => setLoading(false));

    fetch(`${BACKEND_URL}/api/news/for-you`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => { setForYou(Array.isArray(data) ? data : []); setForYouLoading(false); })
      .catch(() => setForYouLoading(false));

    // Pull this user's own submissions so we can show a pending-count nudge
    // next to the Submit button.
    fetch(`${BACKEND_URL}/api/news/submissions/mine`, { credentials: 'include', headers })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setMySubmissions(data); })
      .catch(() => {});
  }, []);

  // Legacy deep-link `?article=news_id` (e.g. from Keli or old shared URLs)
  // is now redirected to the dedicated /news/:newsId page so the experience
  // is consistent: the article opens with its own URL and banner-style hero
  // rather than the old modal.
  useEffect(() => {
    if (!articleParam) return;
    navigate(`/news/${articleParam}`, { replace: true });
  }, [articleParam, navigate]);

  const refreshMySubmissions = () => {
    fetch(`${BACKEND_URL}/api/news/submissions/mine`, { credentials: 'include', headers })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setMySubmissions(data); })
      .catch(() => {});
  };

  const handleReact = async (newsId, reaction) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${newsId}/react`, {
        method: 'POST', credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction }),
      });
      if (!res.ok) throw new Error('Failed');
      const result = await res.json();
      const updateItem = (item) => {
        if (item.news_id !== newsId) return item;
        const prev = item.my_reaction;
        let up = item.thumbs_up || 0;
        let down = item.thumbs_down || 0;
        if (result.action === 'removed') {
          if (prev === 'up') up--; else if (prev === 'down') down--;
        } else if (result.action === 'switched') {
          if (prev === 'up') { up--; down++; } else { down--; up++; }
        } else {
          if (reaction === 'up') up++; else down++;
        }
        return { ...item, thumbs_up: Math.max(0, up), thumbs_down: Math.max(0, down), my_reaction: result.reaction };
      };
      setNews(prev => prev.map(updateItem));
      setForYou(prev => prev.map(updateItem));
    } catch {
      toast.error('Failed to submit reaction');
    }
  };

  const handleDelete = async (item) => {
    const isRss = item.source_type !== 'admin';
    const confirmMsg = isRss
      ? `Remove "${item.title}" from the news feed?\n\nThis article will not be re-imported by future RSS syncs.`
      : `Delete "${item.title}"?\n\nThis cannot be undone.`;
    if (!(await window.appConfirm(confirmMsg))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${item.news_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setNews(prev => prev.filter(n => n.news_id !== item.news_id));
      setForYou(prev => prev.filter(n => n.news_id !== item.news_id));
      toast.success(isRss ? 'Article removed and blocklisted' : 'News story deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  // Filtered list memoised so pagination can derive total pages without
  // re-running the predicate per render.
  const filteredNews = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return news.filter(item => {
      if (filter === 'sll-members' && !item.is_sll_member_news) return false;
      if (filter === 'featured' && !item.is_featured) return false;
      if (filter === 'rss' && item.source_type === 'admin') return false;
      if (q) {
        const hay = `${item.title || ''} ${item.summary || ''} ${item.content || ''} ${item.source || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [news, filter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filteredNews.slice(pageStart, pageStart + PAGE_SIZE);

  // Reset to page 1 whenever the filter or search changes
  useEffect(() => { setPage(1); }, [filter, searchTerm]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    // Scroll to the top of the grid so the user sees page 1 of the new view
    if (gridTopRef.current) {
      gridTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Build a compact list of page numbers with ellipses for very long ranges.
  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const out = [];
    let prev = 0;
    Array.from(pages).filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b).forEach(p => {
      if (prev && p - prev > 1) out.push('…');
      out.push(p);
      prev = p;
    });
    return out;
  }, [totalPages, currentPage]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="news" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading mb-1 md:mb-2 gradient-text">Latest News</h1>
              <p className="text-slate-600">Stay updated with sustainability and logistics trends</p>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-1">
              <Button
                onClick={() => setShowSubmit(true)}
                data-testid="submit-news-btn"
                className="bg-brand-navy hover:bg-brand-dark text-white shadow-sm self-start sm:self-auto"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit an article
              </Button>
              {mySubmissions.pending_count > 0 && (
                <span
                  className="text-xs text-slate-500 flex items-center gap-1"
                  data-testid="submit-news-pending-count"
                >
                  <Clock className="h-3 w-3" />
                  {mySubmissions.pending_count} of yours awaiting review
                </span>
              )}
            </div>
          </div>

          {/* For You Section */}
          {forYou.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-brand-navy" />
                <h2 className="text-xl font-bold font-heading">Recommended For You</h2>
                <span className="text-xs text-slate-400 ml-1">Based on your profile</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {forYou.slice(0, 4).map(article => (
                  <ForYouCard key={article.news_id} article={article} onReact={handleReact} onDelete={handleDelete}
                    onOpen={(a) => navigate(`/news/${a.news_id}`)}
                    isAdmin={user?.role === 'admin'} />
                ))}
              </div>
            </div>
          )}

          {/* All News */}
          <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
            <h2 className="text-xl font-bold font-heading">All News</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="search"
                placeholder="Search…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20 outline-none"
                data-testid="news-search-input"
              />
              <div className="inline-flex rounded-md bg-slate-100 p-0.5" data-testid="news-filter-tabs">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'sll-members', label: 'SLL Member News' },
                  { key: 'featured', label: 'Featured' },
                  { key: 'rss', label: 'RSS' },
                ].map(f => (
                  <button key={f.key}
                    data-testid={`news-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${filter === f.key ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="p-6 border border-slate-200 break-inside-avoid animate-pulse">
                  <div className="w-full h-48 bg-slate-200 rounded-lg mb-4" />
                  <div className="h-3 bg-slate-100 rounded w-32 mb-2" />
                  <div className="h-5 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-full mb-1" />
                  <div className="h-3 bg-slate-100 rounded w-3/4" />
                </Card>
              ))}
            </div>
          ) : (
            <div ref={gridTopRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" data-testid="news-grid">
              {pageItems
                .map(item => {
                const isAdmin = item.source_type === 'admin';
                const isUserSubmission = item.source_type === 'user_submission';
                const externalLink = item.external_url || item.link;
                const hasFullArticle = !!item.content;
                // User submissions open the in-app reader (modal) even when
                // they don't have full `content` — the modal still surfaces
                // the summary, PDF attachment, and external link.
                const canOpenModal = hasFullArticle || isUserSubmission;
                return (
                <Card key={item.news_id} data-testid={`news-card-${item.news_id}`}
                  className={`p-6 relative hover:shadow-lg transition-all flex flex-col h-full ${
                    isAdmin
                      ? 'border-2 border-brand-sage/40 bg-gradient-to-br from-white to-brand-sage/5'
                      : isUserSubmission
                        ? 'border-2 border-brand-blue/30 bg-gradient-to-br from-white to-brand-blue/5'
                        : 'border border-slate-200'
                  }`}>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                      {item.is_featured && (
                        <span className="text-[10px] font-bold text-white bg-brand-sage px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`featured-badge-${item.news_id}`}>
                          <Newspaper className="h-2.5 w-2.5" /> FEATURED
                        </span>
                      )}
                      {item.is_sll_member_news && (
                        <span className="text-[10px] font-bold text-white bg-brand-navy px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`sll-badge-${item.news_id}`}>
                          SLL MEMBER NEWS
                        </span>
                      )}
                      {item.author_name && item.author_name !== item.source && <span className="text-xs text-slate-500">by {item.author_name}</span>}
                    </div>
                  )}
                  {!isAdmin && item.is_sll_member_news && (
                    <span className="text-[10px] font-bold text-white bg-brand-navy px-2 py-0.5 rounded-full inline-flex items-center gap-1 mb-3" data-testid={`sll-badge-${item.news_id}`}>
                      SLL MEMBER NEWS
                    </span>
                  )}
                  {isUserSubmission && (
                    <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                      <span
                        className="text-[10px] font-bold text-white bg-brand-blue px-2 py-0.5 rounded-full flex items-center gap-1"
                        data-testid={`submitted-badge-${item.news_id}`}
                      >
                        <UserCheck className="h-2.5 w-2.5" /> SUBMITTED BY MEMBER
                      </span>
                      {item.submitted_by_name && (
                        <span className="text-xs text-slate-500">via {item.submitted_by_name}</span>
                      )}
                    </div>
                  )}
                  {item.image && (
                    <img src={item.image} alt={item.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                  )}
                  <div className="flex items-center text-xs text-slate-500 mb-2">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(item.published_at).toLocaleDateString()}
                    <span className="mx-2">&middot;</span>
                    <span className="truncate">{item.source}</span>
                  </div>
                  {user?.role === 'admin' && (
                    <button onClick={() => handleDelete(item)}
                      className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-white bg-rose-50 hover:bg-rose-500 border border-rose-200 hover:border-rose-500 px-2.5 py-1 rounded-full transition-all shadow-sm z-10"
                      data-testid={`delete-news-${item.news_id}`}
                      title={isAdmin ? 'Delete this story' : 'Remove this RSS article (will not be re-imported)'}>
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  )}
                  <h3 className={`text-lg font-bold font-heading mb-2 line-clamp-2 min-h-[3.5rem] ${canOpenModal ? 'cursor-pointer hover:text-brand-navy transition-colors' : ''}`}
                    onClick={() => { if (canOpenModal) navigate(`/news/${item.news_id}`); }}
                    data-testid={`news-title-${item.news_id}`}>{item.title}</h3>
                  <div className="text-sm text-slate-600 mb-4">
                    <RichText html={item.summary} className="card card-3" />
                  </div>
                  {isAdmin && hasFullArticle && item.content !== item.summary && (
                    <div className="text-sm text-slate-500 mb-4">
                      <RichText html={item.content} className="card card-3" />
                    </div>
                  )}
                  {item.topic_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.topic_tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs bg-slate-100 text-slate-600">{tag}</Badge>
                      ))}
                    </div>
                  )}
                  {item.attachments?.length > 0 && (
                    <div className="mb-4 space-y-1.5">
                      <p className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attachments</p>
                      {item.attachments.map(att => (
                        <a key={att.attachment_id}
                          href={`${BACKEND_URL}/api/news/attachments/${att.attachment_id}/download`}
                          className="flex items-center gap-2 text-xs text-brand-navy hover:underline bg-slate-50 rounded px-3 py-1.5 border"
                          data-testid={`download-att-${att.attachment_id}`}>
                          <Download className="h-3 w-3" />
                          <span className="truncate">{att.filename}</span>
                          <span className="text-slate-400 ml-auto">{(att.size / 1024).toFixed(0)} KB</span>
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 flex-wrap mt-auto pt-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {canOpenModal && (
                        <button onClick={() => navigate(`/news/${item.news_id}`)}
                          className="inline-flex items-center text-sm text-brand-navy hover:text-brand-dark font-medium"
                          data-testid={`read-article-${item.news_id}`}>
                          Read more
                        </button>
                      )}
                      {externalLink && (
                        <a href={externalLink} target="_blank" rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                            isAdmin
                              ? 'bg-brand-navy/5 text-brand-navy border-brand-navy/20 hover:bg-brand-navy/10'
                              : 'text-brand-navy hover:underline border-transparent'
                          }`}
                          data-testid={`external-link-${item.news_id}`}>
                          {isAdmin ? 'Open external link' : 'View source'} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`news-thumbsup-${item.news_id}`}
                        onClick={() => handleReact(item.news_id, 'up')}
                        className={`flex items-center gap-1 text-xs transition-colors ${item.my_reaction === 'up' ? 'text-brand-navy font-semibold' : 'text-slate-400 hover:text-brand-navy'}`}
                      >
                        <ThumbsUp className={`h-3.5 w-3.5 ${item.my_reaction === 'up' ? 'fill-brand-navy' : ''}`} /> {item.thumbs_up || 0}
                      </button>
                      <button
                        data-testid={`news-thumbsdown-${item.news_id}`}
                        onClick={() => handleReact(item.news_id, 'down')}
                        className={`flex items-center gap-1 text-xs transition-colors ${item.my_reaction === 'down' ? 'text-red-500 font-semibold' : 'text-slate-400 hover:text-red-400'}`}
                      >
                        <ThumbsDown className={`h-3.5 w-3.5 ${item.my_reaction === 'down' ? 'fill-red-500' : ''}`} /> {item.thumbs_down || 0}
                      </button>
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="news-pagination">
              <p className="text-xs text-slate-500" data-testid="news-pagination-summary">
                Showing <span className="font-semibold text-slate-700">{pageStart + 1}</span>
                –<span className="font-semibold text-slate-700">{Math.min(pageStart + PAGE_SIZE, filteredNews.length)}</span>
                {' '}of <span className="font-semibold text-slate-700">{filteredNews.length}</span> stories
              </p>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="news-pagination-prev"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageButtons.map((p, idx) => (
                  p === '…' ? (
                    <span key={`gap-${idx}`} className="px-2 text-slate-400 select-none">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goToPage(p)}
                      data-testid={`news-pagination-page-${p}`}
                      className={`inline-flex items-center justify-center h-9 min-w-9 px-3 rounded-md text-sm font-medium border transition-colors ${
                        p === currentPage
                          ? 'bg-brand-navy text-white border-brand-navy shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      aria-current={p === currentPage ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  )
                ))}
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="news-pagination-next"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {!loading && filteredNews.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500">No news available at the moment</p>
            </div>
          )}
            </div>
            <PageSideRail placement="news" />
          </div>
        </div>
      </main>

      <SubmitNewsDialog
        open={showSubmit}
        onClose={() => setShowSubmit(false)}
        onSubmitted={() => refreshMySubmissions()}
      />
    </div>
  );
}
